import React, { useState } from "react";
import { Button, Popover } from "antd";
import { BulbOutlined, CalendarOutlined, PauseCircleOutlined, ReadOutlined, SoundOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import type { SpeechState } from "../hooks/useSpeech";
import type { BibleVersion } from "../services/bible";

export type ThemeName = "light" | "dark" | "classic";

interface Props {
  fontSize: number;
  onFontSizeChange: (n: number) => void;
  theme: ThemeName;
  onThemeChange: (t: ThemeName) => void;
  speechSupported: boolean;
  speechState: SpeechState;
  speechCanPlay: boolean;
  onToggleSpeech: () => void;
  versionIds: string[];
  availableVersions: BibleVersion[];
  onToggleVersion: (id: string) => void;
  onOpenMyCheckins: () => void;
}

// 固定底部工具栏（参考 holybible 的 bottom-toolbar）
const BottomToolbar: React.FC<Props> = ({
  fontSize,
  onFontSizeChange,
  theme,
  onThemeChange,
  speechSupported,
  speechState,
  speechCanPlay,
  onToggleSpeech,
  versionIds,
  availableVersions,
  onToggleVersion,
  onOpenMyCheckins,
}) => {
  const intl = useIntl();
  const [versionPickerOpen, setVersionPickerOpen] = useState(false);
  const playing = speechState === "playing";

  const themeContent = (
    <div className="toolbar-popover-row">
      {(["light", "dark", "classic"] as ThemeName[]).map((t) => (
        <Button
          key={t}
          size="small"
          type={theme === t ? "primary" : "default"}
          onClick={() => onThemeChange(t)}
        >
          {intl.formatMessage({ id: `theme.${t}` })}
        </Button>
      ))}
    </div>
  );

  const versionContent = (
    <div className="version-picker-popover">
      <div className="version-picker-header">
        {intl.formatMessage({ id: "settings.versionPicker" })}
        {intl.formatMessage({ id: "settings.versionPickerCount" }, { n: versionIds.length })}
      </div>
      <div className="version-picker-list">
        {(() => {
          const grouped: Record<string, BibleVersion[]> = {};
          availableVersions.forEach((v) => {
            const langKey = v.languageName || v.language;
            if (!grouped[langKey]) grouped[langKey] = [];
            grouped[langKey].push(v);
          });
          return Object.entries(grouped).map(([langName, versions]) => (
            <div key={langName} className="version-group">
              <div className="version-group-title">{langName}</div>
              {versions.map((v) => {
                const checked = versionIds.includes(v.id);
                const disabled = !checked && versionIds.length >= 5;
                const isOnlyOne = versionIds.length === 1 && checked;
                return (
                  <label
                    key={v.id}
                    className={`version-item ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled || isOnlyOne}
                      onChange={() => onToggleVersion(v.id)}
                    />
                    <span className="version-item-name">{v.name}</span>
                    <span className="version-item-id">{v.id}</span>
                  </label>
                );
              })}
            </div>
          ));
        })()}
      </div>
    </div>
  );

  return (
    <div className="bottom-toolbar">
      <button
        type="button"
        className="toolbar-btn"
        title={intl.formatMessage({ id: "settings.fontSize" })}
        onClick={() => onFontSizeChange(Math.max(12, fontSize - 2))}
      >
        A−
      </button>
      <button
        type="button"
        className="toolbar-btn"
        title={intl.formatMessage({ id: "settings.fontSize" })}
        onClick={() => onFontSizeChange(Math.min(28, fontSize + 2))}
      >
        A+
      </button>
      <Popover trigger="click" placement="top" content={themeContent}>
        <button type="button" className="toolbar-btn" title={intl.formatMessage({ id: "settings.theme" })}>
          <BulbOutlined />
        </button>
      </Popover>
      <Popover
        trigger="click"
        placement="top"
        open={versionPickerOpen}
        onOpenChange={setVersionPickerOpen}
        content={versionContent}
      >
        <button
          type="button"
          className={`toolbar-btn ${versionIds.length > 1 ? "toolbar-btn-active" : ""}`}
          title={intl.formatMessage({ id: "settings.version" })}
        >
          <ReadOutlined />
          <span className="version-count">{versionIds.length > 1 ? versionIds.length : ""}</span>
        </button>
      </Popover>
      <button
        type="button"
        className="toolbar-btn"
        title={intl.formatMessage({ id: "myCheckins.title" })}
        onClick={onOpenMyCheckins}
      >
        <CalendarOutlined />
      </button>
      {speechSupported && (
        <button
          type="button"
          className={`toolbar-btn ${playing ? "toolbar-btn-active" : ""}`}
          title={intl.formatMessage({ id: playing ? "settings.stopReading" : "settings.readAloud" })}
          onClick={onToggleSpeech}
          disabled={!speechCanPlay && speechState === "idle"}
        >
          {playing ? <PauseCircleOutlined /> : <SoundOutlined />}
        </button>
      )}
    </div>
  );
};

export default BottomToolbar;
